/**
 * Synthetic window metadata (plan §0.2). Live calendar windows live in §6.5b.
 */
import type { WindowKey } from '@meridian/contracts';

/* Frozen at module scope: `as const` and `Readonly<>` are erased at runtime, so
   an `as`-cast could rewrite WN and silently change every number in the app —
   the same failure signature as the §0.3 seed trap. */
export const WIN = Object.freeze(['1M', '3M', '1Y', '3Y'] as const satisfies readonly WindowKey[]);

/** Window length in years. */
export const WYR: Readonly<Record<WindowKey, number>> = Object.freeze({
  '1M': 1 / 12,
  '3M': 0.25,
  '1Y': 1,
  '3Y': 3,
});

/** Synthetic bar counts only — not the live calendar contract. */
export const WN: Readonly<Record<WindowKey, number>> = Object.freeze({
  '1M': 23,
  '3M': 64,
  '1Y': 126,
  '3Y': 156,
});

/**
 * Live window membership: the last N OPERATIONAL trading days on or before the
 * latest OK `data_as_of` (plan §6.5b). Not `WN` — that is the generator's bar
 * count, and the two agree only at `1M`, so a mistaken import is invisible on
 * the one window a smoke test is most likely to open.
 *
 * Lives in `core` rather than in `packages/ingest` because BOTH sides need it:
 * `ingest` computes metrics over the window, and `apps/api/src/services/series`
 * resolves the same bound for `/v1/securities/:ticker/series`. `api → ingest` is
 * a forbidden edge (plan §3.5a), so a home in `ingest` would mean two copies of
 * a §6.5b contract number.
 */
export const WIN_LIVE_DAYS: Readonly<Record<WindowKey, number>> = Object.freeze({
  '1M': 23,
  '3M': 63,
  '1Y': 252,
  '3Y': 756,
});

/**
 * Minimum usable ALIGNED returns per window (plan §6.5b). Below this the
 * security still gets a `security_window_metrics` row for the window — with all
 * four values `null` — and is excluded from correlation for that window. A
 * missing row and a null-valued row are different claims: the first says
 * "unknown security", the second says "known security, insufficient history".
 */
export const WIN_LIVE_MIN_RETURNS: Readonly<Record<WindowKey, number>> = Object.freeze({
  '1M': 15,
  '3M': 45,
  '1Y': 180,
  '3Y': 500,
});
